import type { Document } from '../page';

interface DocumentListProps {
  documents: Document[];
  onView: (document: Document) => void;
  onDelete: (id: string) => void;
}

export default function DocumentList({ documents, onView, onDelete }: DocumentListProps) {
  const getFileIcon = (type: string) => {
    const icons: Record<string, string> = {
      'PDF': 'ri-file-pdf-line',
      'DOC': 'ri-file-word-line',
      'DOCX': 'ri-file-word-line',
      'XLS': 'ri-file-excel-line',
      'XLSX': 'ri-file-excel-line',
      'PPT': 'ri-file-ppt-line',
      'PPTX': 'ri-file-ppt-line',
      'TXT': 'ri-file-text-line',
      'JPG': 'ri-file-image-line',
      'JPEG': 'ri-file-image-line',
      'PNG': 'ri-file-image-line'
    };
    return icons[type] || 'ri-file-line';
  };

  const getFileColor = (type: string) => {
    const colors: Record<string, string> = {
      'PDF': 'bg-red-100 text-red-600',
      'DOC': 'bg-blue-100 text-blue-600',
      'DOCX': 'bg-blue-100 text-blue-600',
      'XLS': 'bg-green-100 text-green-600',
      'XLSX': 'bg-green-100 text-green-600',
      'PPT': 'bg-orange-100 text-orange-600',
      'PPTX': 'bg-orange-100 text-orange-600',
      'TXT': 'bg-gray-100 text-gray-600',
      'JPG': 'bg-purple-100 text-purple-600',
      'JPEG': 'bg-purple-100 text-purple-600',
      'PNG': 'bg-purple-100 text-purple-600'
    };
    return colors[type] || 'bg-gray-100 text-gray-600';
  };

  if (documents.length === 0) {
    return (
      <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-folder-open-line text-4xl text-gray-400 w-10 h-10 flex items-center justify-center"></i>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No documents found</h3>
        <p className="text-sm text-gray-600">Try adjusting your search or upload a new document</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900">
          All Documents ({documents.length})
        </h2>
      </div>

      <div className="divide-y divide-gray-100">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getFileColor(doc.type)}`}>
                <i className={`${getFileIcon(doc.type)} text-2xl w-6 h-6 flex items-center justify-center`}></i>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">{doc.name}</h3>
                    {doc.description && (
                      <p className="text-xs text-gray-600 mb-2">{doc.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onView(doc)}
                      className="p-2 hover:bg-teal-50 text-teal-600 rounded-lg transition-colors cursor-pointer"
                      title="View document"
                    >
                      <i className="ri-eye-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </button>
                    <a
                      href={doc.url}
                      download={doc.name}
                      className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors cursor-pointer"
                      title="Download document"
                    >
                      <i className="ri-download-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </a>
                    <button
                      onClick={() => onDelete(doc.id)}
                      className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors cursor-pointer"
                      title="Delete document"
                    >
                      <i className="ri-delete-bin-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span className="px-2 py-1 bg-gray-100 rounded font-medium">{doc.category}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line w-3 h-3 flex items-center justify-center"></i>
                    {doc.uploadedBy}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
                    {doc.uploadDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-file-line w-3 h-3 flex items-center justify-center"></i>
                    {doc.size}
                  </span>
                  <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded font-medium">
                    v{doc.version}
                  </span>
                </div>

                {doc.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {doc.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
